/**
 * Glossary Utilities
 * Parse and match terminology rules for translation
 */

import { SynonymGroup } from '../types';

export interface GlossaryRule {
    source: string;
    target: string;
    sourceLower: string; // Pre-computed for matching
}

/**
 * Parse terminology rules from text format
 * Supports: "Source -> Target" or "Source > Target" or "Source -> Target"
 * One rule per line
 */
export function parseTerminologyRules(rulesText: string): GlossaryRule[] {
    if (!rulesText || !rulesText.trim()) return [];

    const lines = rulesText.split('\n');
    const rules: GlossaryRule[] = [];

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) continue;

        // Match patterns: -> or > or -> or => or |
        const match = trimmed.match(/^(.+?)\s*(?:->|->|=>|>|\|)\s*(.+)$/);
        if (match) {
            const source = match[1].trim();
            const target = match[2].trim();
            if (source && target) {
                rules.push({
                    source,
                    target,
                    sourceLower: source.toLowerCase()
                });
            }
        }
    }

    return rules;
}

/**
 * Find glossary rules that match the source text
 * Uses case-insensitive substring matching
 */
export function findMatchingRules(sourceText: string, allRules: GlossaryRule[]): GlossaryRule[] {
    if (!sourceText || allRules.length === 0) return [];

    const textLower = sourceText.toLowerCase();
    const matched: GlossaryRule[] = [];
    const seen = new Set<string>();

    for (const rule of allRules) {
        // Check if rule source appears in the text
        if (textLower.includes(rule.sourceLower)) {
            // Avoid duplicates
            const key = `${rule.sourceLower}::${rule.target.toLowerCase()}`;
            if (!seen.has(key)) {
                seen.add(key);
                matched.push(rule);
            }
        }
    }

    // Sort by source length descending (longer matches first)
    matched.sort((a, b) => b.source.length - a.source.length);

    return matched;
}

/**
 * Find matching rules for multiple segments
 * Returns a deduplicated list of all matched rules
 */
export function findMatchingRulesForSegments(segments: string[], allRules: GlossaryRule[]): GlossaryRule[] {
    if (segments.length === 0 || allRules.length === 0) return [];

    const allText = segments.join(' ');
    return findMatchingRules(allText, allRules);
}

/**
 * Format matched rules for injection into prompt
 */
export function formatRulesForPrompt(rules: GlossaryRule[]): string {
    if (rules.length === 0) return '';

    return rules.map(r => `"${r.source}" -> "${r.target}"`).join('\n');
}

// ========================================
// Synonym Groups -> Flat Text (for AI Prompt)
// ========================================

/**
 * Convert SynonymGroup[] to flat text format for AI prompt extraRules.
 * Each source in a group maps to the default target.
 * Format: "source | target" per line (compatible with Worker parser).
 */
export function synonymGroupsToFlatText(groups: SynonymGroup[]): string {
    if (!groups || groups.length === 0) return '';

    const lines: string[] = [];

    for (const group of groups) {
        const defaultTarget = group.targets.find(t => t.isDefault)?.text || group.targets[0]?.text;
        if (!defaultTarget) continue;

        for (const source of group.sources) {
            if (source.trim()) {
                lines.push(`${source.trim()} | ${defaultTarget}`);
            }
        }
    }

    return lines.join('\n');
}

// ========================================
// Synonym Groups (Many-to-Many Terminology)
// ========================================

/**
 * Find synonym groups that match the source text
 * Returns groups where at least one source term appears in the text
 */
export function findMatchingSynonymGroups(
    sourceText: string,
    groups: SynonymGroup[]
): SynonymGroup[] {
    if (!sourceText || groups.length === 0) return [];

    const textLower = sourceText.toLowerCase();

    return groups.filter(group =>
        group.sources.some(src => textLower.includes(src.toLowerCase()))
    );
}



/**
 * Format synonym groups for injection into AI prompt
 * Provides all source terms and target options with context hints
 */
export function formatSynonymGroupsForPrompt(groups: SynonymGroup[]): string {
    if (groups.length === 0) return '';

    return groups.map(g => {
        const sources = g.sources.map(s => `"${s}"`).join(', ');
        const defaultTarget = g.targets.find(t => t.isDefault)?.text || g.targets[0]?.text || '';

        // Simplified format: "Source 1", "Source 2" -> "Target"
        return `${sources} -> "${defaultTarget}"`;
    }).join('\n');
}

/**
 * Convert legacy one-to-one rules to synonym groups (many-to-many)
 * Uses union-find to merge rules that share common sources OR targets
 * Example: "HĐQT → Board members" + "HĐQT → Board member" → 1 group with sources=[HĐQT], targets=[Board members, Board member]
 */
export function convertRulesToSynonymGroups(rules: GlossaryRule[]): SynonymGroup[] {
    if (rules.length === 0) return [];

    // Union-Find
    const parent = new Map<number, number>();
    const find = (i: number): number => {
        if (!parent.has(i)) parent.set(i, i);
        if (parent.get(i) !== i) parent.set(i, find(parent.get(i)!));
        return parent.get(i)!;
    };
    const union = (a: number, b: number) => {
        const ra = find(a), rb = find(b);
        if (ra !== rb) parent.set(ra, rb);
    };

    // Build index: source → rule indices, target → rule indices
    const sourceIndex = new Map<string, number[]>();
    const targetIndex = new Map<string, number[]>();

    rules.forEach((rule, i) => {
        parent.set(i, i);
        const srcKey = rule.source.toLowerCase();
        const tgtKey = rule.target.toLowerCase();

        if (!sourceIndex.has(srcKey)) sourceIndex.set(srcKey, []);
        sourceIndex.get(srcKey)!.push(i);

        if (!targetIndex.has(tgtKey)) targetIndex.set(tgtKey, []);
        targetIndex.get(tgtKey)!.push(i);
    });

    // Union rules that share the same source
    for (const indices of sourceIndex.values()) {
        for (let j = 1; j < indices.length; j++) {
            union(indices[0], indices[j]);
        }
    }

    // Union rules that share the same target
    for (const indices of targetIndex.values()) {
        for (let j = 1; j < indices.length; j++) {
            union(indices[0], indices[j]);
        }
    }

    // Group rules by root
    const components = new Map<number, number[]>();
    rules.forEach((_, i) => {
        const root = find(i);
        if (!components.has(root)) components.set(root, []);
        components.get(root)!.push(i);
    });

    // Create SynonymGroups from components
    const groups: SynonymGroup[] = [];
    let idCounter = 1;

    for (const indices of components.values()) {
        const seenSources = new Set<string>();
        const seenTargets = new Set<string>();
        const sources: string[] = [];
        const targets: { text: string; isDefault: boolean }[] = [];

        for (const i of indices) {
            const rule = rules[i];
            const srcLower = rule.source.toLowerCase();
            const tgtLower = rule.target.toLowerCase();

            if (!seenSources.has(srcLower)) {
                seenSources.add(srcLower);
                sources.push(rule.source);
            }
            if (!seenTargets.has(tgtLower)) {
                seenTargets.add(tgtLower);
                targets.push({ text: rule.target, isDefault: targets.length === 0 });
            }
        }

        // Name = first target (most common representation)
        const name = targets[0]?.text || sources[0] || 'Unknown';
        groups.push({
            id: `migrated-${idCounter++}`,
            name,
            sources,
            targets
        });
    }

    return groups;
}
/**
 * Parse Synonym Groups from TSV (Excel paste)
 * Logic: Group by first source term.
 */
export function parseSynonymGroupsFromTSV(tsvText: string): SynonymGroup[] {
    const groupsMap = new Map<string, SynonymGroup>();

    const lines = tsvText.split(/\r?\n/);

    for (const line of lines) {
        if (!line.trim()) continue;

        // Tab separated canonical: Sources | Target | Context | IsDefault | Category | SourceLang | TargetLang
        const parts = line.split('\t');
        if (parts.length < 2) continue; // Need at least Sources and Target

        const sourceStr = parts[0].trim();
        const targetStr = parts[1].trim();
        const context = parts[2]?.trim() || '';
        // Check for 'yes', 'true', '1', 'x' (case insensitive)
        const isDefaultRaw = parts[3]?.trim().toLowerCase() || '';
        const isDefault = ['yes', 'true', '1', 'x', 'y'].includes(isDefaultRaw);
        const category = parts[4]?.trim() || '';
        const sourceLang = parts[5]?.trim().toLowerCase() || '';
        const targetLang = parts[6]?.trim().toLowerCase() || '';

        if (!sourceStr || !targetStr) continue;

        // Split sources by comma or pipe
        const sources = sourceStr.split(/[,|]/).map(s => s.trim()).filter(Boolean);
        if (sources.length === 0) continue;

        // Group Name logic: Use the first source term
        const groupName = sources[0];

        // Create or get group
        if (!groupsMap.has(groupName.toLowerCase())) {
            groupsMap.set(groupName.toLowerCase(), {
                id: crypto.randomUUID(),
                name: groupName, // Keep original case for name
                sources: [],
                targets: [],
                category: category || undefined,
                sourceLang: sourceLang || undefined,
                targetLang: targetLang || undefined,
            });
        }

        const group = groupsMap.get(groupName.toLowerCase())!;
        // Apply category + lang nếu chưa set (first wins)
        if (category && !group.category) group.category = category;
        if (sourceLang && !group.sourceLang) group.sourceLang = sourceLang;
        if (targetLang && !group.targetLang) group.targetLang = targetLang;

        // Merge sources (avoid duplicates)
        sources.forEach(s => {
            // Case-insensitive check for existence, but store mixed-case if new
            const exists = group.sources.some(existing => existing.toLowerCase() === s.toLowerCase());
            if (!exists) {
                group.sources.push(s);
            }
        });

        // Add target variant
        const targetExists = group.targets.some(t =>
            t.text === targetStr && t.context === context
        );

        if (!targetExists) {
            group.targets.push({
                id: crypto.randomUUID(),
                text: targetStr,
                context: context,
                isDefault: isDefault
            });
        } else if (isDefault) {
            // If duplicate but this one is marked default, update the existing one
            const existing = group.targets.find(t => t.text === targetStr && t.context === context);
            if (existing) existing.isDefault = true;
        }
    }

    return Array.from(groupsMap.values());
}

/**
 * Find Synonym Groups that match the source text
 * Returns unique groups that appear in the text
 */
/**
 * Normalize dashes and special characters for flexible matching.
 * Converts en dash, em dash, horizontal bar, minus sign, etc. to regular hyphen.
 */
function normalizeDashes(text: string): string {
    // Replace various dash-like characters with regular hyphen
    return text.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-');
}

export function findMatchingSynonymGroupsForSegments(
    sourceText: string,
    groups: SynonymGroup[],
    protectedPatterns: string[] = []
): { group: SynonymGroup; match: string; context?: string }[] {
    if (!sourceText || groups.length === 0) return [];

    const matches: { group: SynonymGroup; match: string; context?: string }[] = [];
    const seenGroupIds = new Set<string>();
    const lowerText = normalizeDashes(sourceText.toLowerCase());

    for (const group of groups) {
        if (seenGroupIds.has(group.id)) continue;

        for (const source of group.sources) {
            if (!source) continue;
            const sourceLower = normalizeDashes(source.toLowerCase());

            if (lowerText.includes(sourceLower)) {
                matches.push({
                    group,
                    match: source,
                    context: group.targets.find(t => t.isDefault)?.context
                });
                seenGroupIds.add(group.id);
                break;
            }
        }
    }

    return matches;
}
